import { useState } from "react";

import { useForm } from "react-hook-form";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { RecaptchaAction } from "@/common/constants/recaptcha";
import { trpc } from "@/common/helpers/trpc";
import { isTrpcTooManyRequestsError } from "@/common/helpers/trpcRateLimit";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useRecaptchaRetry } from "@/common/hooks/useRecaptchaRetry";
import { useTranslate } from "@/common/hooks/useTranslate";

export type ContactUsFormData = {
  senderEmail: string;
  message: string;
};

export const useContactUsForm = ({ organizationPageId }: { organizationPageId: string }) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { executeTrpc } = useRecaptchaRetry();
  const { mutateAsync: sendContactMessage } = trpc.sendOrganizationPageContactMessage.useMutation();

  const form = useForm<ContactUsFormData>({ defaultValues: { senderEmail: "", message: "" } });
  const [sendErrorMessage, setSendErrorMessage] = useState<string | null>(null);
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);

  const submit = form.handleSubmit(async ({ senderEmail, message }) => {
    setSendErrorMessage(null);
    const { error } = await executeTrpc(
      RecaptchaAction.SendOrganizationPageContactMessage,
      senderEmail,
      sendContactMessage,
      { organizationPageId, senderEmail, message }
    );

    if (error) {
      setSendErrorMessage(
        isTrpcTooManyRequestsError({ data: error })
          ? t("organization", "contactUs.tooManyMessages")
          : t("organization", "contactUs.sendFailed")
      );
      return;
    }

    logAmplitudeEvent(AmplitudeEvents.OrgPageContactMessageSent);
    setSentToEmail(senderEmail);
  });

  const reset = () => {
    form.reset();
    setSendErrorMessage(null);
    setSentToEmail(null);
  };

  return { form, submit, sendErrorMessage, sentToEmail, reset };
};
